// Pure helpers for the activity-heatmap visualization. The backend returns
// only cells with `messages > 0` to keep the wire payload bounded — the
// frontend backfills the missing 24×7 cells before rendering. Kept as a
// separate module so they can be unit-tested without rendering React.

import type { ContentHeatmapCell } from "@community-analytics/shared";

/**
 * Number of weekday rows (Mon..Sun) and hour-of-day columns (0..23). ISO
 * ordering: Mon = 0, Sun = 6 — matches the backend's `dayOfWeek` field.
 */
export const HEATMAP_DAYS = 7;
export const HEATMAP_HOURS = 24;

/**
 * Backfill a sparse cell list into a dense 7×24 grid of message counts.
 * Out-of-range cells (defensive guard against a future backend bug) are
 * silently dropped — the grid never resizes. Returns `grid[day][hour]`.
 */
export function denseHeatmap(
  cells: ReadonlyArray<ContentHeatmapCell>,
): number[][] {
  const grid: number[][] = Array.from({ length: HEATMAP_DAYS }, () =>
    Array.from({ length: HEATMAP_HOURS }, () => 0),
  );
  for (const cell of cells) {
    if (
      cell.dayOfWeek < 0 ||
      cell.dayOfWeek >= HEATMAP_DAYS ||
      cell.hour < 0 ||
      cell.hour >= HEATMAP_HOURS
    ) {
      continue;
    }
    const row = grid[cell.dayOfWeek];
    if (!row) continue;
    row[cell.hour] = cell.messages;
  }
  return grid;
}

/**
 * Maximum observed `messages` count across all cells. Used as the color-
 * scale upper bound. Returns 0 for an empty heatmap (caller renders an
 * empty state instead of dividing by zero).
 */
export function heatmapMaxMessages(
  cells: ReadonlyArray<ContentHeatmapCell>,
): number {
  let max = 0;
  for (const cell of cells) {
    if (cell.messages > max) max = cell.messages;
  }
  return max;
}

/**
 * Map a value in [0, max] to a 0..1 intensity ratio. `max <= 0` returns 0
 * (no activity, transparent cell). Saturates at 1 if `value` exceeds `max`
 * (defensive — should not happen in practice).
 */
export function heatmapIntensity(value: number, max: number): number {
  if (max <= 0) return 0;
  if (value <= 0) return 0;
  if (value >= max) return 1;
  return value / max;
}
